import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { trackUiEvent } from '../analytics'
import { ROUTES } from '../routes'
import { useGalleryPreload } from '../GalleryPreloadContext'
import Button from './Button'
import SourceCard from './SourceCard'
import { EmptyState, InlineAlert, PageHeader, Skeleton } from './ui'
import '../styles/source-gallery.css'

const HIDDEN_SLUGS = new Set(['iiko', 'moysklad'])
const CONNECTOR_ORDER = ['bitrix24', 'amocrm']

function normalizeConnection (connection) {
  if (connection.status === 'error' && /token|401|unauthori[sz]ed/i.test(connection.error_message || '')) {
    return { ...connection, status: 'reauth_required' }
  }
  return connection
}

export default function ConnectorGallery () {
  const navigate = useNavigate()
  const preload = useGalleryPreload()
  const [connectors, setConnectors] = useState(preload?.connectors || [])
  const [connections, setConnections] = useState((preload?.connections || []).filter(item => item.status !== 'inactive').map(normalizeConnection))
  const [loading, setLoading] = useState(!preload)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [disconnecting, setDisconnecting] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [connectorResult, connectionResult] = await Promise.all([
        api.getConnectors(),
        api.getConnections()
      ])
      setConnectors(connectorResult.connectors || [])
      setConnections((connectionResult.connections || []).filter(item => item.status !== 'inactive').map(normalizeConnection))
      trackUiEvent('source_gallery_viewed', {
        available_count: (connectorResult.connectors || []).filter(item => !HIDDEN_SLUGS.has(item.slug)).length
      })
    } catch (err) {
      setError(err.message)
      trackUiEvent('source_gallery_load_failed', { message: err.message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (preload) {
      trackUiEvent('source_gallery_viewed', {
        available_count: preload.connectors.filter(item => !HIDDEN_SLUGS.has(item.slug)).length
      })
      return
    }
    load()
  }, [load, preload])

  const visibleConnectors = useMemo(
    () => connectors
      .filter(connector => !HIDDEN_SLUGS.has(connector.slug))
      .sort((left, right) => {
        const leftIndex = CONNECTOR_ORDER.indexOf(left.slug)
        const rightIndex = CONNECTOR_ORDER.indexOf(right.slug)
        return (leftIndex === -1 ? CONNECTOR_ORDER.length : leftIndex) -
          (rightIndex === -1 ? CONNECTOR_ORDER.length : rightIndex)
      }),
    [connectors]
  )

  function connect (slug) {
    trackUiEvent('source_connect_started', { connector_slug: slug })
    navigate(ROUTES.CONNECT(slug))
  }

  function open (slug) {
    trackUiEvent('source_opened', { connector_slug: slug })
    navigate(ROUTES.INTEGRATION(slug))
  }

  async function disconnect (connector, connection) {
    if (!window.confirm(`Отключить ${connector.name}? Сценарии и сопоставления сохранятся.`)) return
    setDisconnecting(connection.id)
    setNotice(null)
    try {
      await api.disconnectConnection(connection.id)
      setConnections(current => current.filter(item => item.id !== connection.id))
      setNotice(`${connector.name} отключён`)
      trackUiEvent('source_disconnected', { connector_slug: connector.slug })
    } catch (err) {
      setNotice(`Не удалось отключить ${connector.name}: ${err.message}`)
      trackUiEvent('source_disconnect_failed', { connector_slug: connector.slug })
    } finally {
      setDisconnecting(null)
    }
  }

  if (loading) {
    return (
      <div aria-label='Загрузка источников'>
        <PageHeader title='Источники' description='Подключите источник один раз — все его сценарии используют это подключение' />
        <div className='source-gallery__grid'>
          <Skeleton className='source-gallery__skeleton' />
          <Skeleton className='source-gallery__skeleton' />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <InlineAlert title='Не удалось загрузить источники' tone='error'>
        <p>{error}</p>
        <Button variant='stroke' onClick={load}>Повторить</Button>
      </InlineAlert>
    )
  }

  return (
    <div className='source-gallery'>
      <PageHeader title='Источники' description='Подключите источник один раз — все его сценарии используют это подключение' />
      {notice && <InlineAlert tone={notice.startsWith('Не удалось') ? 'error' : 'info'}>{notice}</InlineAlert>}
      {visibleConnectors.length === 0
        ? <EmptyState title='Источники пока недоступны' description='Попробуйте повторить позже.' />
        : (
          <div className='source-gallery__grid'>
            {visibleConnectors.map(connector => {
              const connection = connections.find(item => item.slug === connector.slug)
              return (
                <SourceCard
                  key={connector.id}
                  connector={connector}
                  connection={connection}
                  disconnecting={disconnecting === connection?.id}
                  onConnect={() => connect(connector.slug)}
                  onOpen={() => open(connector.slug)}
                  onDisconnect={() => disconnect(connector, connection)}
                />
              )
            })}
          </div>
          )}
    </div>
  )
}
